import React, { useEffect, useRef } from "react";
import CircularProgress from "@material-ui/core/CircularProgress";
import Button from "@material-ui/core/Button";
import { AZUL, ERRO_BORDA, ERRO_FUNDO, FONTE, NAVY, SUAVE } from "../estilos/tokens";
import { useAlturaDisponivel } from "../hooks/useAlturaDisponivel";

interface Props {
  carregando: boolean;
  erro: string | null;
  vazio?: boolean;
  onRecarregar: () => void;
  children: React.ReactNode;
}

const caixa: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  gap: 16,
  minHeight: 320,
  fontFamily: FONTE,
};

export const faixaErro: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 16,
  flexWrap: "wrap",
  fontFamily: FONTE,
  fontSize: 15,
  color: NAVY,
  background: ERRO_FUNDO,
  border: `1px solid ${ERRO_BORDA}`,
  borderRadius: 8,
  padding: "12px 16px",
  marginBottom: 14,
};

const regiaoViva: React.CSSProperties = {
  position: "absolute",
  width: 1,
  height: 1,
  overflow: "hidden",
  clip: "rect(0 0 0 0)",
  whiteSpace: "nowrap",
};

const EstadoRequisicao = ({ carregando, erro, vazio, onRecarregar, children }: Props) => {
  const { referencia: refCaixa, altura: alturaDisponivel } = useAlturaDisponivel();
  const alturaMinima = alturaDisponivel || caixa.minHeight;
  const caixaCheia: React.CSSProperties = { ...caixa, minHeight: alturaMinima };

  const temConteudo = React.Children.toArray(children).length > 0;
  const mostraErro = !carregando && !!erro;
  const erroSobreConteudo = mostraErro && temConteudo;
  const mostraVazio = !carregando && !erro && !!vazio;

  const conteudoOculto = carregando || mostraVazio || (mostraErro && !temConteudo);

  const refConteudo = useRef<HTMLDivElement | null>(null);
  const refCarregando = useRef<HTMLDivElement | null>(null);
  const focoAnterior = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const ativo = document.activeElement as HTMLElement | null;
    if (carregando) {
      if (ativo && refConteudo.current && refConteudo.current.contains(ativo)) {
        focoAnterior.current = ativo;
        if (refCarregando.current) {
          refCarregando.current.focus();
        }
      }
      return;
    }
    const origem = focoAnterior.current;
    focoAnterior.current = null;
    if (origem && document.body.contains(origem)) {
      origem.focus();
    }
  }, [carregando]);

  return (
    <div ref={refCaixa} aria-busy={carregando}>
      <div style={regiaoViva} role="status" aria-live="polite">
        {carregando ? "Carregando..." : ""}
      </div>

      {erroSobreConteudo && (
        <div style={faixaErro}>
          <span>{erro}</span>
          <Button
            variant="outlined"
            size="small"
            onClick={onRecarregar}
            style={{ fontFamily: FONTE, color: AZUL, borderColor: AZUL, fontWeight: 500, textTransform: "none" }}
          >
            Tentar novamente
          </Button>
        </div>
      )}

      {mostraErro && !temConteudo && (
        <div style={caixaCheia}>
          <span style={{ fontSize: 19, fontWeight: 500, color: NAVY, textAlign: "center", maxWidth: 480 }}>{erro}</span>
          <Button
            variant="outlined"
            onClick={onRecarregar}
            style={{ fontFamily: FONTE, color: AZUL, borderColor: AZUL, fontWeight: 500, textTransform: "none" }}
          >
            Tentar novamente
          </Button>
        </div>
      )}

      {mostraVazio && (
        <div style={caixaCheia}>
          <span style={{ fontSize: 19, fontWeight: 500, color: SUAVE, textAlign: "center", maxWidth: 480 }}>
            Nenhum dado para o período e filtros selecionados.
          </span>
        </div>
      )}

      {carregando && (
        <div ref={refCarregando} tabIndex={-1} style={{ ...caixaCheia, outline: "none" }}>
          <CircularProgress style={{ color: AZUL }} />
          <span style={{ fontSize: 18, fontWeight: 500, color: SUAVE }}>Carregando...</span>
        </div>
      )}

      <div ref={refConteudo} style={conteudoOculto ? { display: "none" } : undefined}>
        {children}
      </div>
    </div>
  );
};

export default EstadoRequisicao;
